import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Icons,
  useTheme,
  Divider,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import { toggleStarSpace } from '@core/spaceSelector/operations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { changeClassRoom } from '@core/actions/classRoom';
import { NAVIGATION_SOURCE_STARRED_SPACES } from '@core/constants/analytics';
import { trackHomeViewAllClicked, trackHomeWidgetItemClicked } from '../common';
import {
  getHomeStarredSpaces,
  getHomeSpacesLoading,
  getHomeSpacesErrorType,
  getHomeAssignableSpacesForView,
  isHomeViewRoleReady,
  getHomeViewRole,
} from '@app/selectors/home';
import { fetchHomeSpaces } from '@app/actions/home/fetchHomeSpaces';
import push from '@app/utils/routing';

import { useResponsiveSpacesNavigation } from '@app/hooks/useResponsiveSpacesNavigation';

import SpaceListSkeleton from '../common/SpaceListSkeleton';
import StarredSpaceItem from './StarredSpaceItem';
import { getStarredSpaceColors } from '../../colors';
import StarredSpacesEmptyState from './StarredSpacesEmptyState';
import { WidgetCard, WidgetErrorState } from '../common';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';
import { saveHomeScrollPosition } from '../../homeScrollPosition';

const STRINGS = {
  emptyState: 'No starred spaces.',
  title: 'Starred spaces',
  hide: 'Hide',
  viewAll: 'View all starred spaces',
  widgetKey: 'home-starred-spaces-widget',
  helpArticleUrl:
    'https://aula.zendesk.com/hc/en-us/articles/15852659347218--Starred-Spaces',
};

const ITEM_RENDER_COUNT = 4;

const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const StarredSpacesWidget = () => {
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<any>();
  const { openSpacesList } = useResponsiveSpacesNavigation('starred');

  const isLoading = useSelector(getHomeSpacesLoading);
  const errorType = useSelector(getHomeSpacesErrorType);
  const isViewRoleReady = useSelector(isHomeViewRoleReady);
  const role = useSelector(getHomeViewRole);
  const rawSpaces = useSelector(getHomeStarredSpaces);
  const assignableSpaces = useSelector(getHomeAssignableSpacesForView);

  const spaces = useMemo(() => {
    return rawSpaces.map((s: any) => ({
      id: s.objectId,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      avatar: s.avatar || null,
      lastNavigation: s.lastNavigation,
      isStaffAccess: s.isStaffAccess || false,
      isAssigned: s.isAssigned,
      archived: s.archived || false,
    }));
  }, [rawSpaces]);

  useTrackHomeWidgetLoaded(
    'starred_spaces',
    // classRooms are ready once the home view role is ready; do not gate on
    // getHomeSpacesLoading (defaults false) or student-only users fire at 0.
    isViewRoleReady && !errorType,
    spaces.length
  );

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
  };

  const handleUnstar = useCallback(
    (id: string, index?: number) => {
      dispatch(
        toggleStarSpace({
          spaceId: id,
          navigationSource: NAVIGATION_SOURCE_STARRED_SPACES,
          itemIndex: index,
        })
      );
    },
    [dispatch]
  );

  const handleSpaceClick = useCallback(
    async (id: string, index?: number) => {
      trackHomeWidgetItemClicked({
        navigationSource: NAVIGATION_SOURCE_STARRED_SPACES,
        itemType: 'Spaces',
        itemId: id,
        spaceId: id,
        itemIndex: index,
        role,
      });
      const space = spaces.find((s) => s.id === id);
      if (space?.lastNavigation) {
        let navigationUrl = space.lastNavigation.replace(/#/, '');
        await dispatch(changeClassRoom(id) as any);
        const [pathname, search] = navigationUrl.split('?');
        const queryParams = new URLSearchParams(search || '');
        queryParams.set('from', 'home');

        push({
          pathname,
          search: `?${queryParams.toString()}`,
          state: { fromHome: true },
        });
      } else {
        push({
          pathname: `/dashboard/${id}/myspacenav`,
          search: '?from=home',
          state: { fromHome: true },
        });
      }
    },
    [spaces, dispatch]
  );

  const handleRetry = useCallback(() => {
    dispatch(fetchHomeSpaces() as any);
  }, [dispatch]);

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <SpaceListSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (errorType) {
      return (
        <WidgetErrorState
          type={errorType === 'network' ? 'offline' : errorType}
          onRetry={handleRetry}
          isRetrying={isLoading}
        />
      );
    }

    if (spaces.length === 0) {
      return (
        <StarredSpacesEmptyState
          helpArticleUrl={STRINGS.helpArticleUrl}
          onTrigger={
            assignableSpaces.length > 0
              ? () => openSpacesList('all_from_starred' as any)
              : undefined
          }
        />
      );
    }

    const visibleSpaces = spaces.slice(0, ITEM_RENDER_COUNT);

    return visibleSpaces.map((space, index) => (
      <React.Fragment key={space.id}>
        <StarredSpaceItem
          space={space}
          onUnstar={(id) => handleUnstar(id, index)}
          onClick={(id) => handleSpaceClick(id, index)}
          isStaffAccess={space.isStaffAccess}
        />
        {index < visibleSpaces.length - 1 && <Divider />}
      </React.Fragment>
    ));
  }, [
    isLoading,
    errorType,
    spaces,
    handleUnstar,
    handleSpaceClick,
    openSpacesList,
    handleRetry,
    assignableSpaces.length,
  ]);

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const footer = (
    <Button
      fullWidth
      kind="secondary"
      endIcon={<ArrowForwardIcon />}
      sx={{
        width: '100%',
        color: getStarredSpaceColors(palette).starIcon,
      }}
      onClick={() => {
        if (isMobile) {
          saveHomeScrollPosition();
        }
        trackHomeViewAllClicked(
          NAVIGATION_SOURCE_STARRED_SPACES,
          spaces.length
        );
        openSpacesList('starred');
      }}
    >
      {STRINGS.viewAll}
    </Button>
  );

  const footerUI = spaces.length > ITEM_RENDER_COUNT ? footer : null;

  const titleLeading = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      mr={1}
      flexShrink={0}
      sx={{
        borderRadius: '100px',
        bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#EAE8ED',
        color: 'primary.main',
      }}
    >
      <Icons.StarBorder sx={{ fontSize: 28 }} />
    </Box>
  );

  return (
    <WidgetCard
      title={STRINGS.title}
      titleLeading={titleLeading}
      menuActions={menuActions}
      footer={footerUI}
    >
      {content}
    </WidgetCard>
  );
};

export default StarredSpacesWidget;
